/**
 * Rename… / Move… of a Data Lake path (S8 Task 8.10; D3, E4, findings S8-02 / S8-03 / S8-14 / S8-R2-06). The source's
 * identity + ETag come from `useMoveSource` (the listed ETag of a Data Lake row, else a resolve at open — Blob-view rows
 * always resolve); Submit stays disabled until one exists, and the SAME ETag is sent. The service refuses an existing
 * destination and a changed source; the dialog reports partial / uncertain moves honestly (Continue / Check again) and
 * never claims the move is atomic. Closing cancels the in-flight request.
 */
import { useForm, useStore } from '@tanstack/react-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import type { HnsPathRef } from '../../../../../types/azureStorage';
import { storageContainersQueryKey, type StorageListingSnapshot } from '../../../../database-objects/services/azureStorageListing';
import { readStorageRecoverability } from '../../mutations/storageMutations';
import { useReportStorageDialogGuard } from '../../properties/dialogs/storageDialogStore';
import { moveSourceLine, useMoveSource } from '../useMoveSource';
import type { HnsHostedDialogProps } from './hnsDialogTypes';
import { hnsLabel, initialMoveDraft, moveDestination, moveRulesText, softDeleteRenameText, type MoveDraft } from './moveDialogModel';
import { MovePathFields } from './MovePathFields';
import { MoveStatusNotice } from './MoveStatusNotice';
import { useMovePathActions } from './useMovePathActions';

/** The destination file systems: the account's containers from the tree's snapshot, always including the source's. */
function useFileSystems(connectionId: string, current: string): string[] {
  const queryClient = useQueryClient();
  return useMemo(() => {
    const snapshot = queryClient.getQueryData<StorageListingSnapshot>(storageContainersQueryKey(connectionId));
    const names = (snapshot?.items ?? []).flatMap((item) => (item.kind === 'container' && !item.deleted && !item.name.startsWith('$') ? [item.name] : []));
    return [...new Set([current, ...names])].sort((left, right) => left.localeCompare(right));
  }, [queryClient, connectionId, current]);
}

export function MovePathDialog({ connectionId, resource, options, onGuardChange, onClose }: HnsHostedDialogProps) {
  const action = options.moveAction ?? 'move';
  const { source, reload } = useMoveSource({ connectionId, resource, listedEtag: options.listedEtag, tabId: options.tabId });
  const fileSystems = useFileSystems(connectionId, resource.fileSystem);
  // S8.0 exit change 9 (row M10): the soft-delete sentence for a directory while blob soft delete is on or unknown.
  const recoverability = useQuery({ queryKey: ['azure-storage', connectionId, 'recoverability'], queryFn: () => readStorageRecoverability(connectionId), enabled: resource.isDirectory, staleTime: 60_000, retry: false });
  const softDelete = recoverability.data?.blobSoftDelete.state ?? 'unknown';
  const { onDone } = options;
  const finished = useCallback((moved: HnsPathRef) => { onDone?.(moved); onClose(); }, [onDone, onClose]);
  const actions = useMovePathActions({ connectionId, resource, action, origin: options.origin ?? 'row-menu', tabId: options.tabId, onFinished: finished });
  const initial = useMemo(() => initialMoveDraft(resource, action), [resource, action]);
  const form = useForm({
    defaultValues: initial,
    onSubmit: async ({ value }) => {
      const target = moveDestination(resource, value);
      if (source.state !== 'ready' || target.ok === false) return;
      await actions.submit(source.resource, source.etag, target.destination);
    },
  });
  const values = useStore(form.store, (state) => state.values);
  const target = moveDestination(resource, values);
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);
  const started = actions.status.kind === 'partial' || actions.status.kind === 'uncertain';
  useReportStorageDialogGuard(onGuardChange, dirty || started, actions.pending);
  const close = () => { actions.cancel(); onClose(); };
  const reloadSource = () => { actions.resetStatus(); reload(); };
  // U37: a taken destination name is fixed by typing another one — focus the field instead of offering a Reload.
  const destinationTaken = actions.status.kind === 'error' && actions.status.error.code === 'DestinationExists';
  useEffect(() => {
    if (!destinationTaken) return;
    const field = document.getElementById(action === 'rename' ? 'storage-hns-move-name' : 'storage-hns-move-path');
    if (field instanceof HTMLInputElement) { field.focus(); field.select(); }
  }, [destinationTaken, action]);
  const change = (next: Partial<MoveDraft>) => {
    for (const [key, value] of Object.entries(next) as Array<[keyof MoveDraft, string]>) form.setFieldValue(key, value);
  };
  const submitReason = source.state !== 'ready' ? 'Waiting for the source to be checked' : actions.pending ? 'The move is in flight' : target.ok === false ? target.reason : undefined;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-lg" data-testid="storage-hns-move-dialog" data-action={action}>
        <DialogHeader>
          <DialogTitle>{action === 'rename' ? 'Rename' : 'Move'} {resource.isDirectory ? 'folder' : 'file'}</DialogTitle>
          <DialogDescription>{moveRulesText(resource)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }} className="grid gap-3" data-testid="storage-hns-move-form">
          <p className={source.state === 'error' ? 'text-sm text-destructive break-words' : 'text-sm text-muted-foreground break-words'} data-testid="storage-hns-move-source" data-state={source.state} data-via={source.state === 'ready' ? source.via : undefined}>
            {moveSourceLine(resource, source, action)}
          </p>
          <MovePathFields draft={values} fileSystems={fileSystems} frozen={started} onChange={change} />
          {dirty && target.ok === false && <p className="text-xs text-destructive" data-testid="storage-hns-move-invalid">{target.reason}</p>}
          {target.ok === true && <p className="text-xs text-muted-foreground" data-testid="storage-hns-move-destination">Destination: {hnsLabel(target.destination)}</p>}
          {resource.isDirectory && softDelete !== 'off' && <p className="text-xs text-muted-foreground" data-testid="storage-hns-move-soft-delete">{softDeleteRenameText(resource)}</p>}
          <MoveStatusNotice status={actions.status} source={resource} pending={actions.pending} onReload={reloadSource} onContinue={() => void actions.continueMove()} onRecheck={() => void actions.recheck()} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} data-testid="storage-hns-move-cancel">{started ? 'Close' : 'Cancel'}</Button>
            {!started && <Button type="submit" disabled={!!submitReason} title={submitReason} data-testid="storage-hns-move-submit">{action === 'rename' ? 'Rename' : 'Move'}</Button>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
